
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

type LightboxType = 'unlimited' | 'eliteVip';

interface UseUpsellLightboxProps {
  isLoggedIn: boolean;
  isAdmin?: boolean;
  userHasUnlimited?: boolean;
  userHasEliteVIP?: boolean;
}

export const useUpsellLightbox = ({ 
  isLoggedIn, 
  isAdmin = false,
  userHasUnlimited = false,
  userHasEliteVIP = false 
}: UseUpsellLightboxProps) => {
  const [showUnlimitedLightbox, setShowUnlimitedLightbox] = useState(false);
  const [showEliteVIPLightbox, setShowEliteVIPLightbox] = useState(false);
  const [loginCount, setLoginCount] = useState(0);
  const [lastLightboxShown, setLastLightboxShown] = useState<Date | null>(null);
  const location = useLocation();

  // Only show lightboxes when on the dashboard
  const isDashboard = location.pathname === '/dashboard';
  
  // Don't show during purchase flow or redirects
  const isPurchasePage = location.pathname.includes('/purchase');
  const isRedirectPath = location.pathname === '/' || location.pathname === '/login';

  // Load login count from local storage on initial render
  useEffect(() => {
    if (isLoggedIn && !isAdmin) { // Don't count logins for admin users
      const storedCount = localStorage.getItem('loginCount');
      const storedLastShown = localStorage.getItem('lastLightboxShown');
      
      if (storedCount) {
        setLoginCount(parseInt(storedCount, 10) + 1);
      } else {
        setLoginCount(1);
      }
      
      if (storedLastShown) {
        setLastLightboxShown(new Date(storedLastShown));
      }
    }
  }, [isLoggedIn, isAdmin]);

  // Save login count to local storage whenever it changes
  useEffect(() => {
    if (isLoggedIn && loginCount > 0 && !isAdmin) { // Skip for admin users
      localStorage.setItem('loginCount', loginCount.toString());
      
      // Show lightboxes based on login count
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      
      const shouldShowWeekly = lastLightboxShown === null || lastLightboxShown < oneWeekAgo;
      
      // Only show lightboxes on the dashboard and not during purchase flow, and never for admins
      // Also don't show if user already owns the product
      if (isDashboard && !isPurchasePage && !isRedirectPath && !isAdmin) {
        // For Unlimited Development lightbox - $599
        // Only show on second login (not first), every fourth login, then weekly
        // And only if user doesn't already have the unlimited membership
        if (!userHasUnlimited && (loginCount === 2 || loginCount % 4 === 0) && (shouldShowWeekly || loginCount === 2)) {
          setShowUnlimitedLightbox(true);
          setLastLightboxShown(new Date());
          localStorage.setItem('lastLightboxShown', new Date().toISOString());
        } 
        // For Elite VIP lightbox - $4,999
        // Show on third login (not first), every fifth login, then weekly
        // And only if user doesn't already have the elite VIP membership
        else if (!userHasEliteVIP && (loginCount === 3 || loginCount % 5 === 0) && (shouldShowWeekly || loginCount === 3)) {
          setShowEliteVIPLightbox(true);
          setLastLightboxShown(new Date());
          localStorage.setItem('lastLightboxShown', new Date().toISOString());
        }
      }
    }
  }, [isLoggedIn, loginCount, lastLightboxShown, isDashboard, isPurchasePage, isRedirectPath, isAdmin, userHasUnlimited, userHasEliteVIP]);

  const closeUnlimitedLightbox = () => setShowUnlimitedLightbox(false);
  const closeEliteVIPLightbox = () => setShowEliteVIPLightbox(false);

  return {
    showUnlimitedLightbox,
    showEliteVIPLightbox,
    closeUnlimitedLightbox,
    closeEliteVIPLightbox
  };
};
